import React, { useState, useEffect } from "react";
import { reduxForm, Field, getFormAsyncErrors } from "redux-form";
import PropTypes from "prop-types";
import { connect, useDispatch } from "react-redux";

import {
	Modal,
	Button,
	ErrorBanner,
	ReduxControls,
	ProgressIndicator
} from "rhombus-react";
import {
	versionNameExists,
	validVersionNameCharacters,
	versionNameMaxLength } from "widgets/forms/validators/Versions";
import { requiredNotNull } from "widgets/forms/validators/validators";

import { DUPLICATE_VERSION_FORM } from "widgets/forms/list";
import { loadCreative } from "reducers/CreativeDetails";
import { CreativeVersions } from "reducers/CreativeVersions";
import { loadVersionDetails } from "reducers/VersionDetails";
import { noop } from "utils/common";

import "./DuplicateVersionModal.css";

const { duplicateVersion } = CreativeVersions;

function DuplicateVersionModal(props){
	const dispatch = useDispatch();

	let { libraryId,
		creativeId,
		onClose,
		handleSubmit,
		asyncValidating,
		selectedVersion,
		errorMessages,
		versionDetails,
		 } = props;

	const commonModalProps = {
		title:  "Duplicate Version",
		type: "modal",
		className: "duplicate-version-modal",
		xClose: true,
		allowFullScreen: false,
		bladeProps: {
			verticallyCenter: true,
		},
		onClose: closeModal,
		setAppTitle: false,
	};

	let [attemptedSubmit, setAttemptedSubmit] = useState(false)
		, shouldShowErrors =  errorMessages && (errorMessages?.length !== 0)
		, $errorBanner = null
		, [duplicateProps, setDuplicateProps] = useState({})
		;

	useEffect(() => {
		if(!selectedVersion.id) { return ; }

		dispatch(loadVersionDetails(libraryId, creativeId, selectedVersion.id))
			.then((res) => {
				if(!res?.success){ return; }

				const { data } = res?.payload || {}
					, { variableConfig } = selectedVersion;

				if(variableConfig){
					Object.keys(variableConfig).map((key) => {
						variableConfig[key] = data.find(v => v.name === key);
					});

					setDuplicateProps(Object.assign({ libraryId, creativeId }, { data: selectedVersion }));
				}
			});
	}, [libraryId, creativeId, selectedVersion]);

	$errorBanner = shouldShowErrors && (
		<ErrorBanner
			title="A version already exists with this name"
			messages={errorMessages}
		/>
	);

	function closeModal(){
		onClose();
	}

	const onSubmit = handleSubmit((values)=>{
		let { duplicatedVersionName } = values;
		setAttemptedSubmit(true);
		dispatch(duplicateVersion({ newName:duplicatedVersionName, ...duplicateProps }))
			.then((resp) => {
				if(resp.success){
					dispatch(loadCreative(creativeId));
					return closeModal();
				}
			});
	});

	function Body() {
		return (
			<div className="body-container">
				{<ProgressIndicator isLoading={ !versionDetails?.isLoaded } />}
				<form className="form" id={ DUPLICATE_VERSION_FORM } onSubmit={ onSubmit }>
					<div className={`form-group ${DUPLICATE_VERSION_FORM}`} >
						{$errorBanner}
						<Field
							name="duplicatedVersionName"
							label="Name for Duplicated Version"
							component={ReduxControls.Input}
							validate={[validVersionNameCharacters, versionNameMaxLength, requiredNotNull]}
						/>
					</div>
				</form>
			</div>
		);
	}

	function Footer() {
		const submitProps = {
			type: "submit",
			form: DUPLICATE_VERSION_FORM,
			disabled: Boolean(
				shouldShowErrors || attemptedSubmit || asyncValidating || !versionDetails?.isLoaded
			),
		};

		if(attemptedSubmit){
			submitProps.icon = "spinner";
			submitProps.className = "is-submitting";
		}

		const $cxlButton = (<Button theme="default" key="cancel" onClick = { closeModal } >Cancel</Button>);
		const $dupButton = (
			<Button key="submit" { ...submitProps } >
				Duplicate
			</Button>
		);

		return [$cxlButton, $dupButton];
	}


	return (
		<Modal
			{...commonModalProps}
			renderContent = { Body }
			renderFooter = { Footer }
		/>
	);
}

function asyncValidate(values, dispatch, props, blurredField){
	let { duplicatedVersionName } = values;
	let { libraryId, creativeId } = props;

	return versionNameExists(dispatch, libraryId, creativeId, duplicatedVersionName, "duplicatedVersionName");
}

function mapStateToProps(state, ownProps) {
	let { versionId } = ownProps
		, { items } = state.creativeVersions
		, selectedVersion = items?.find(ea=>ea.id===parseInt(versionId)) || { }
		, { versionDetails } = state
		, { name } = selectedVersion || ""
		, duplicatedVersionName
		;

	// Build the initial duplicate name
	if (name) {
		const [newVersionName] = name.split('.');
		if (newVersionName) {
			duplicatedVersionName = `${newVersionName} copy`;
		}
	}

	return {
		initialValues: {
			duplicatedVersionName,
		},
		selectedVersion,
		versionDetails,
		errorMessages:getFormAsyncErrors(DUPLICATE_VERSION_FORM)(state),
	};
}

const ConnectedDuplicateVersionModal=reduxForm({
	form: DUPLICATE_VERSION_FORM,
	asyncValidate,
	asyncBlurFields: [],
	shouldAsyncValidate: () => true, // This makes it validate the default suggested name if you never touched the field
})(DuplicateVersionModal);

export default connect(mapStateToProps, null)(ConnectedDuplicateVersionModal);

DuplicateVersionModal.displayName = "DuplicateVersionModal";
DuplicateVersionModal.propTypes = {
	libraryId: PropTypes.string.isRequired,
	creativeId: PropTypes.string.isRequired,
	versionId: PropTypes.string.isRequired,
	onClose: PropTypes.func,
};
DuplicateVersionModal.defaultProps = {
	onClose: noop,
};

export const PRIVATE ={
	mapStateToProps,
	asyncValidate,
};
